import { createContext, useContext } from 'react';
import type { Theme } from './theme';

export interface MetricThreshold {
  good: number;
  bad: number;
  invert?: boolean;
}

export interface BrandTheme {
  /** Semantic colour overrides, merged over the base DARK/LIGHT palette. */
  colors?: Partial<Theme>;
  /** Categorical chart palette. Falls back to DEFAULT_PALETTE. */
  palette?: string[];
  /** Per-metric colour thresholds, keyed by metric key ('cpl', 'cpc', ...). Empty by default. */
  thresholds?: Record<string, MetricThreshold>;
}

/** Categorical chart palette - keep distinct hues for legibility. */
export const DEFAULT_PALETTE: string[] = [
  '#2563eb',
  '#22c55e',
  '#f59e0b',
  '#a855f7',
  '#ef4444',
  '#06b6d4',
  '#ec4899',
  '#84cc16',
];

export const DEFAULT_BRAND_THEME: BrandTheme = {};

/** MG's historical CPL band. Seed data for MG's brand row - NOT a default for anyone else. */
export const MG_CPL_THRESHOLDS: MetricThreshold = {
  good: 110,
  bad: 160,
  invert: true,
};

export const BrandThemeContext = createContext<BrandTheme>(DEFAULT_BRAND_THEME);
export const BrandThemeProvider = BrandThemeContext.Provider;
export const useBrandTheme = (): BrandTheme => useContext(BrandThemeContext);

export function resolveTheme(base: Theme, brand: BrandTheme): Theme {
  return { ...base, ...brand.colors };
}

export function resolvePalette(brand: BrandTheme): string[] {
  return brand.palette ?? DEFAULT_PALETTE;
}

export function metricColor(
  value: number,
  threshold: MetricThreshold | undefined,
  theme: Theme,
): string {
  if (value <= 0 || !threshold) return theme.muted;

  const { good, bad, invert } = threshold;
  if (invert) {
    if (value <= good) return theme.good;
    if (value >= bad) return theme.bad;
    return theme.warn;
  }

  if (value >= good) return theme.good;
  if (value <= bad) return theme.bad;
  return theme.warn;
}
